const afterFooter = {
  setSpacingCssVariables: function () {
    // Set responsive variables
    const announcement = document.querySelector('.g-announcement')
    const header = document.querySelector('.g-header')
    const footer = document.querySelector('.g-footer')

    let rafId = null
    const readHeights = () => ({
      announcementHeight: announcement ? announcement.offsetHeight : 0,
      headerHeight: header ? header.offsetHeight : 0,
      footerHeight: footer ? footer.offsetHeight : 0,
      viewportHeight: window.innerHeight,
    })

    const writeHeights = ({ announcementHeight, headerHeight, footerHeight, viewportHeight }) => {
      root.style.setProperty('--s-vp-height-js', `${viewportHeight}px`)
      root.style.setProperty('--s-announcement', `${announcementHeight}px`)
      root.style.setProperty('--s-header', `${headerHeight}px`)
      root.style.setProperty('--s-footer', `${footerHeight}px`)
    }

    const updateResponsiveVariables = () => {
      const measurements = readHeights()
      writeHeights(measurements)
    }

    const scheduleUpdate = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        updateResponsiveVariables()
      })
    }

    updateResponsiveVariables()
    window.addEventListener('resize', scheduleUpdate)
  },
  avoidStyleFlash: function () {
    // .avoid-style-flash elements turns visible
    setTimeout(() => {
      document.querySelectorAll('.avoid-style-flash').forEach((el) => {
        el.style.visibility = 'visible'
      })
    }, 400)
  },
  init: function () {
    root.classList.add('is-loaded')
    this.setSpacingCssVariables()
    this.avoidStyleFlash()
  },
}

const ariaFocusManager = {
  tabindexCache: new WeakMap(),
  observer: null,

  focusableSelectors: [
    'a[href]',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'details',
    '[contenteditable]',
    '[tabindex]:not([disabled])',
  ].join(', '),

  updateFocusableElements: function (container = document) {
    const elementsWithAriaHidden = container.querySelectorAll('[aria-hidden]')

    // Also check if container itself has aria-hidden
    if (container.hasAttribute?.('aria-hidden')) {
      this.processElement(container)
    }

    elementsWithAriaHidden.forEach((element) => {
      this.processElement(element)
    })
  },

  isEffectivelyHidden: function (element) {
    let current = element

    while (current && current !== document.body) {
      if (current.getAttribute('aria-hidden') === 'true') {
        return true
      }
      current = current.parentElement
    }

    return false
  },

  processElement: function (element) {
    const focusableChildren = element.querySelectorAll(this.focusableSelectors)

    focusableChildren.forEach((child) => {
      // Cache the original tabindex value if not already cached
      if (!this.tabindexCache.has(child)) {
        const originalValue = child.getAttribute('tabindex')
        this.tabindexCache.set(child, originalValue)
      }

      // Check if this child is effectively hidden (any ancestor has aria-hidden="true")
      const shouldBeHidden = this.isEffectivelyHidden(child)

      if (shouldBeHidden) {
        child.setAttribute('tabindex', '-1')
      } else {
        this.restoreTabindex(child)
      }
    })
  },

  restoreTabindex: function (element) {
    const originalTabindex = this.tabindexCache.get(element)

    if (originalTabindex === null) {
      element.removeAttribute('tabindex')
    } else {
      element.setAttribute('tabindex', originalTabindex)
    }
  },

  observeAriaHiddenChanges: function () {
    this.observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          this.processElement(mutation.target)
        }

        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.updateFocusableElements(node)
            }
          })
        }
      })
    })

    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true,
    })
  },

  destroy: function () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },

  init: function () {
    this.updateFocusableElements()
    this.observeAriaHiddenChanges()
  },
}

const focusTrapManager = {
  activeTraps: [],

  activate: function (container, trigger = null) {
    if (!container) return

    const trap = {
      container,
      trigger,
      triggeredByKeyboard: this.wasKeyboardTriggered(),
      boundHandler: null,
    }

    trap.boundHandler = this.handleKeydown.bind(this, trap)
    document.addEventListener('keydown', trap.boundHandler)

    this.activeTraps.push(trap)
  },

  deactivate: function (container, closedByKeyboard = false) {
    const trapIndex = this.activeTraps.findIndex((t) => t.container === container)
    if (trapIndex === -1) return

    const trap = this.activeTraps[trapIndex]

    document.removeEventListener('keydown', trap.boundHandler)

    if (trap.trigger && (trap.triggeredByKeyboard || closedByKeyboard)) {
      trap.trigger.focus()
    }

    this.activeTraps.splice(trapIndex, 1)
  },

  handleKeydown: function (trap, e) {
    if (e.key === 'Tab') {
      this.handleTab(trap.container, e)
    }
  },

  handleTab: function (container, e) {
    const focusable = this.getFocusableElements(container)
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    // Check if focus is currently outside the container
    const isOutside = !container.contains(active)

    if (isOutside) {
      // Redirect focus into the container
      e.preventDefault()
      if (e.shiftKey) {
        last.focus()
      } else {
        first.focus()
      }
      return
    }

    // Normal trap cycling within container
    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (active === last) {
        e.preventDefault()
        first.focus()
      }
    }
  },

  getFocusableElements: function (container) {
    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ]

    return [...container.querySelectorAll(selectors.join(', '))].filter((el) => el.offsetParent !== null)
  },

  wasKeyboardTriggered: function () {
    return document.activeElement?.matches(':focus-visible')
  },
}

const initFields = () => {
  const fields = `.c-field input[type="text"],
			.c-field input[type="email"],
			.c-field input[type="tel"],
			.c-field input[type="url"],
			.c-field input[type="password"],
			.c-field input[type="file"],
			.c-field textarea`
  const updateState = (target) => {
    target.value ? target.classList.add('is-contain-value') : target.classList.remove('is-contain-value')
  }

  on('body', 'change', fields, (e) => {
    const target = e.target
    updateState(target)
  })

  document.querySelectorAll(fields).forEach((el) => {
    updateState(el)
  })
}

const initWYSIWYG = () => {
  // iframe fit vids
  reframe(
    `.wysiwyg iframe[src*="vimeo.com"],
		.wysiwyg iframe[src*="youtube.com"],
		.wysiwyg-page iframe[src*="vimeo.com"],
		.wysiwyg-page iframe[src*="youtube.com"]`,
    'video-reframe'
  )

  // instagram aspect ratio
  instagramIframeInterval = setInterval(() => {
    const iframes = document.querySelectorAll('.wysiwyg .instagram-media')
    const iframesLoaded = document.querySelectorAll('.wysiwyg .instagram-media.is-loaded')

    if (iframes.length == iframesLoaded.length) {
      clearInterval(instagramIframeInterval)
    }

    iframes.forEach((el) => {
      if (!el.classList.contains('is-loaded') && parseInt(el.getAttribute('height')) > 0) {
        el.style.height = `${el.getAttribute('height')}px`
        el.classList.add('is-loaded')
      }
    })
  }, 500)
}

const initPageTransition = () => {
  const getHrefType = (el) => {
    const href = el.href
    const isInternalLink = href.startsWith('/') || href.includes(siteUrl)
    const isAnchorLink = href.startsWith('#') || href.split('#')[0] == location.href.split('#')[0]

    if (isAnchorLink) {
      return 'anchor'
    } else if (isInternalLink) {
      return 'internal'
    } else {
      return 'external'
    }
  }

  // trigger internal link page transition
  on('body', 'click', 'a[href]', (e) => {
    const target = e.target.closest('a[href]')
    const isKeyPressed = e.shiftKey || e.ctrlKey || e.altKey || e.metaKey
    const isNewTab = target == '_blank'

    target.blur()

    if (getHrefType(target) == 'internal' && !isNewTab && !isKeyPressed) {
      e.preventDefault()

      // remove active overlays
      root.classList.remove('is-header-sticky', 'is-menu-active', 'is-mobile-menu-active', 'is-megamenu-active')
      root.classList.add('is-leaving')

      location.href = target.href
    }
  })

  // auto assign noopener and _blank to external links
  document.querySelectorAll('a[href]').forEach((el) => {
    if (getHrefType(el) == 'external') {
      el.rel = 'noopener'
      el.target = el.target || '_blank'
    }
  })
}

const loader = {
  // Adds a new loader to an element
  new: function (el) {
    const loader = document.createElement('div')
    loader.classList.add('c-loader')
    el.appendChild(loader)
  },
  updateLoader: function (loader, progress) {
    loader.dataset.progress = progress
    loader.style.transform = `scaleX(${progress / 100})`
  },
  // Update loader progress and animate progress bar
  updateProgress: function (el) {
    const loader = el.querySelector('.c-loader')

    if (!loader) {
      this.new(el)
      setTimeout(() => this.updateProgress(el), 1)
      return
    }

    if (loader.dataset.intervalId) {
      clearInterval(parseInt(loader.dataset.intervalId))
    }

    if (!loader.dataset.progress || parseInt(loader.dataset.progress) >= 100) {
      this.updateLoader(loader, getRandomInt(10, 30))
    }

    const intervalId = setInterval(() => {
      const currentProgress = parseInt(loader.dataset.progress)

      // Stop at 90 and wait for explicit complete() call
      if (currentProgress >= 90) {
        clearInterval(intervalId)
        delete loader.dataset.intervalId
        return
      }

      const newProgress = currentProgress + getRandomInt(5, 10)
      this.updateLoader(loader, Math.min(newProgress, 90))
    }, 1000)

    loader.dataset.intervalId = intervalId
  },
  // Completes loader progress and hides loader
  complete: function (el) {
    const loader = el.querySelector('.c-loader')
    this.updateLoader(loader, 100)
    setTimeout(() => {
      this.updateLoader(loader, 101)
      if (loader.dataset.intervalId) {
        clearInterval(parseInt(loader.dataset.intervalId))
        delete loader.dataset.intervalId
      }
    }, 400)
  },
  init: function () {
    this.new(root)
  },
}

// ***EXECUTE FUNCTIONS***
afterFooter.init()
loader.init()
initFields()
initPageTransition()

// "DOMContentLoaded" waits for plugins to be complete parsing and executing
window.addEventListener('DOMContentLoaded', () => {
  initWYSIWYG()
  scrollHandler = new ScrollHandler()
  ariaFocusManager.init()
})

// NOTE:
// `window.onload` fires only after the entire page has finished loading
// (DOM, scripts, styles, images, fonts, iframes).
// This makes it one of the latest lifecycle events.
//
// Safe to put here:
// - non-critical scripts
// - analytics / tracking
// - lazy hydration
// - observers
// - background tasks
//
// Avoid putting here if needed for initial layout, interaction,
// or anything that should run before first paint
window.addEventListener('load', () => {
  root.classList.add('is-smooth-scroll')
})
